import { injectable, injectFromBase } from 'inversify'
import { RepoSettings } from '@/decorators/repositoryDecorators'
import type { Endpoint } from '@/services/endpoints'
import OrmApiRepository from '@/data/repositories/ormApiRepository'
import Counter from '@/data/models/tables/Counter'
import { TmRepositoryError } from '@/core/error/tmRepositoryError'
import type { PaginationUrlType } from '@/services/tables/types'
import type { PaginationParams } from '@/services/tables/pagination/types'
import { httpBuildQuerySerializer } from '@/utils/url'
import type { Dict } from '@/types'

@RepoSettings<Endpoint>({
  model: Counter,
})
@injectable()
@injectFromBase()
export default class CounterRepository<T extends Counter> extends OrmApiRepository<T> {
  public async fetchCount(
    id: string,
    queryParameterBag: PaginationUrlType,
    paginationParamsBag?: PaginationParams,
    searchQuery?: string,
    searchFields?: string[],
  ) {
    if (!this.hasCountEndpoint()) {
      throw new TmRepositoryError('Please define count endpoint in settings()')
    }
    const params = this.buildCountRequestParams(queryParameterBag, paginationParamsBag, searchQuery, searchFields)
    const url = this.endpointsService.getPath(this.getEndpoint()!)
    const result = await this.fetchRequestCount(url, params)

    await this.insertOrUpdate([{ ...result.data, id }])
  }

  protected buildCountRequestParams(
    queryParameterBag: PaginationUrlType,
    paginationParamsBag?: PaginationParams,
    searchQuery?: string,
    searchFields?: string[],
  ): Record<string, unknown> {
    return Object.assign(
      this.createGridRequestParams(queryParameterBag, paginationParamsBag, searchQuery, searchFields),
      this.filterQueryService.serializeFiltersToBracketNotation({ filters: queryParameterBag.filter }),
    )
  }

  protected fetchRequestCount(url: string, params: Dict) {
    return this.getApiSource().get<T>(url, {
      params,
      paramsSerializer: (serializableParams: Dict) => httpBuildQuerySerializer(serializableParams),
    })
  }

  protected async fetchCountWithBody(id: string, requestBody: Dict) {
    if (!this.hasCountEndpoint()) {
      throw new TmRepositoryError('Please define count endpoint in settings()')
    }
    const url = this.endpointsService.getPath(this.getEndpoint()!)
    const result = await this.getApiSource().post<T>(url, requestBody)

    await this.insertOrUpdate([{ ...result.data, id }])
  }

  protected getEndpoint() {
    return this.settings().countEndpoint
  }

  protected hasCountEndpoint(): boolean {
    return !!this.getEndpoint()
  }
}
